import type { EventCallback } from '@tauri-apps/api/event'
import axios, { CancelTokenSource, AxiosResponse, AxiosProgressEvent, InternalAxiosRequestConfig } from 'axios'


import { useLocalization } from '@/shared/composables/localization'
import { useStoreEnvSettings } from '@/shared/store/environment'
import { EventProgress } from '@/shared/types/tauri'
import { getAppURL } from '@/shared/utils/electron'
import { uploadFile, useFetchApi } from '@/shared/utils/fetch'
import { tauriMode } from '@/shared/utils/import-meta'

interface UsePromptServer {
  saveFile: (
    file: File,
    path: string,
    fileName: string,
    onProgress?: (event: AxiosProgressEvent | EventCallback<EventProgress>) => void
  ) => Promise<void | AxiosResponse<unknown>>;
  saveAvatar: (file: File, path: string, fileName: string) => Promise<string>;
  abortSaveFile: (file: File) => void;
  removeFile: (fileName: string) => Promise<void>
  maxUploadFileSize: () => number
}

const BASE_URL = '/files'
const DEFAULT_MAX_FILE_SIZE = 104857600
const getAbortSourceKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`
const filesAbortSources = new Map<string, CancelTokenSource>()

export function usePromptServer (): UsePromptServer {
  const { t } = useLocalization()
  const { getSettingByKey } = useStoreEnvSettings()
  const maxUploadFileSize = (): number => {
    const fromDb = getSettingByKey<number>('maxUploadFileSize')
    return fromDb ? fromDb.value : DEFAULT_MAX_FILE_SIZE
  }

  const saveFile = async (
    file: File,
    path: string,
    fileName: string,
    onProgress?: (event: AxiosProgressEvent | EventCallback<EventProgress>) => void,
  ) => {
    if (file.size > maxUploadFileSize()) {
      return Promise.reject(new Error(`${t('common.maxAllowedFileSize')} ${maxUploadFileSize() / 1024 / 1024} ${t('common.mb')}`))
    }
    if (tauriMode()) {
      await import('@tauri-apps/api/webviewWindow').then(({ getCurrentWebviewWindow }) => getCurrentWebviewWindow().listen('UPLOAD_PROGRESS', (event) => {
        if (onProgress) {
          onProgress(event as unknown as EventCallback<EventProgress>)
        }
      }))
      return uploadFile(`${BASE_URL}/${fileName}`, path, 'PUT', {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })
    } else {
      const source: CancelTokenSource = axios['CancelToken'].source()
      const config = {
        cancelToken: source.token,
      } as InternalAxiosRequestConfig
      filesAbortSources.set(getAbortSourceKey(file), source)

      if (onProgress) {
        config.onUploadProgress = onProgress
      }

      const promise = axios.put(getAppURL({ pathName: `${BASE_URL}/${fileName}` }), file, config)

      promise.finally(() => {
        filesAbortSources.delete(getAbortSourceKey(file))
      })

      return promise
    }
  }

  const abortSaveFile = (file: File) => {
    // TODO: tauri http client не поддерживает отмену запросов
    const key = getAbortSourceKey(file)
    const source = filesAbortSources.get(key)
    if (source) {
      source.cancel()
      filesAbortSources.delete(key)
    }
  }

  const saveAvatar = (
    file: File,
    path: string,
    fileName: string,
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      saveFile(file, path, fileName)
        .then(() => {
          resolve(`${BASE_URL}/${fileName}`)
        })
        .catch((error: Error) => {
          reject(error)
        })
    })
  }

  const removeFile = async (fileName: string) => {
    try {
      await useFetchApi().delete(`${BASE_URL}/${fileName}`)
    } catch(e) {
      console.error(e)
    }
  }

  return {
    saveFile,
    saveAvatar,
    abortSaveFile,
    removeFile,
    maxUploadFileSize,
  }
}
